import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import axios from 'axios'
import { STORAGE_TOKEN_KEY } from '@/constants/auth'
import { showToast } from '@/utils/toast'

export const REQUEST_TOKEN_KEY = 'Authorization'

/** 统一 API 响应包装 */
export interface ResponseVO<T = unknown> {
  flag: boolean
  code: string
  message: string
  data: T
}

export type RequestError = AxiosError<ResponseVO>

/** 后端 API 统一前缀 */
export const API_BASE_PREFIX = '/cap'

const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? API_BASE_PREFIX,
  timeout: 6000,
})

export function createParams(params: Record<string, string | number | boolean>): string {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    query.set(key, String(value))
  })
  return `?${query.toString()}`
}

function normalizeBearerToken(raw: string): string {
  const trimmed = raw.trim()
  return trimmed.startsWith('Bearer ') ? trimmed : `Bearer ${trimmed}`
}

function getStoredToken(): string | null {
  return sessionStorage.getItem(STORAGE_TOKEN_KEY)
}

export function setStoredToken(token: string): void {
  sessionStorage.setItem(STORAGE_TOKEN_KEY, normalizeBearerToken(token))
}

export function clearStoredToken(): void {
  sessionStorage.removeItem(STORAGE_TOKEN_KEY)
}

function notifyAuthExpired(): void {
  const wx = (
    window as Window & {
      wx?: { miniProgram?: { postMessage: (payload: { data: unknown }) => void } }
    }
  ).wx
  wx?.miniProgram?.postMessage?.({ data: { type: 'AUTH_EXPIRED' } })
}

function getErrorMessage(data: ResponseVO | undefined, fallback: string): string {
  return data?.message?.trim() || fallback
}

function errorHandler(error: RequestError): Promise<never> {
  if (error.response) {
    const { data, status } = error.response

    if (status === 403) {
      showToast(getErrorMessage(data, '无权限访问'))
      return Promise.reject(error)
    }

    if (status === 401) {
      clearStoredToken()
      showToast(getErrorMessage(data, '登录已过期，请重新进入'))
      notifyAuthExpired()
      return Promise.reject(error)
    }

    showToast(getErrorMessage(data, '请求失败，请稍后再试'))
    return Promise.reject(error)
  }

  showToast('网络异常，请检查网络后重试')
  return Promise.reject(error)
}

function requestHandler(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> {
  const savedToken = getStoredToken()
  if (savedToken) {
    config.headers[REQUEST_TOKEN_KEY] = normalizeBearerToken(savedToken)
  }
  return config
}

function responseHandler(response: AxiosResponse<ResponseVO>) {
  const { code, message } = response.data

  if (code === '0') {
    return response.data
  }

  showToast(message || '请求失败，请稍后再试')
  return Promise.reject(new Error(message || '请求失败，请稍后再试'))
}

request.interceptors.request.use(requestHandler, errorHandler)
request.interceptors.response.use(
  responseHandler as (response: AxiosResponse<ResponseVO>) => AxiosResponse | Promise<AxiosResponse>,
  errorHandler
)

export function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<ResponseVO<T>> {
  return request.get<T, ResponseVO<T>>(url, config)
}

export function apiPost<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<ResponseVO<T>> {
  return request.post<T, ResponseVO<T>>(url, data, config)
}

export default request
